import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Copy, Loader2, RefreshCw } from 'lucide-react';
import {
  BrandConversionSetup as BrandConversionSetupData,
  createBrandConversionTestClick,
  getBrandConversionSetup,
  rotateBrandConversionKey,
} from '../../api/payment/brand-deal.api';
import { copyToClipboard } from '../../utils/common/copy';
import { maskSecret } from '../../utils/brand-conversion';

// Mục "Cài mã đo chuyển đổi" (doanh nghiệp, chi tiết chiến dịch, spec 2026-10-05-brand-conversion §3):
// đoạn script copy (nguyên văn từ BE), hướng dẫn 3 bước, ví dụ API máy chủ, khoá công khai, khoá bí mật
// che/hiện một lần, "Tạo lại khoá" (có xác nhận), "Tạo lượt bấm thử" (mở tab mới), "Lần thử gần nhất".

const errorMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

const formatDateTime = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const fmtVnd = (n: number | null) => (n == null ? '—' : `${Number(n).toLocaleString('vi-VN')}đ`);

const copyWithToast = async (text: string, label: string) => {
  const ok = await copyToClipboard(text);
  if (ok) toast.success(`Đã sao chép ${label}.`);
  else toast.error(`Không sao chép được ${label}.`);
};

interface BrandConversionSetupProps {
  campaignId: string;
}

const BrandConversionSetupPanel: React.FC<BrandConversionSetupProps> = ({ campaignId }) => {
  const [setup, setSetup] = useState<BrandConversionSetupData | null>(null);
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);
  const [testingClick, setTestingClick] = useState(false);
  const [refreshingTest, setRefreshingTest] = useState(false);

  const load = () => {
    setLoading(true);
    getBrandConversionSetup(campaignId)
      .then(setSetup)
      .catch((e) => toast.error(errorMessage(e, 'Không tải được mục cài mã đo chuyển đổi.')))
      .finally(() => setLoading(false));
  };

  useEffect(load, [campaignId]);

  const rotate = async () => {
    if (!window.confirm('Khoá cũ sẽ ngừng hoạt động ngay.')) return;
    try {
      setRotating(true);
      const next = await rotateBrandConversionKey(campaignId);
      setSetup(next);
      toast.success('Đã tạo lại khoá.');
    } catch (e) {
      toast.error(errorMessage(e, 'Tạo lại khoá thất bại.'));
    } finally {
      setRotating(false);
    }
  };

  const testClick = async () => {
    try {
      setTestingClick(true);
      const res = await createBrandConversionTestClick(campaignId);
      window.open(res.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast.error(errorMessage(e, 'Tạo lượt bấm thử thất bại.'));
    } finally {
      setTestingClick(false);
    }
  };

  // Final-review I1: chỉ gộp `lastTest` vào state hiện có — KHÔNG ghi đè nguyên `setup` bằng response
  // GET mới (GET sau lần sinh/tạo lại đầu tiên không còn `secret` đầy đủ, ghi đè nguyên sẽ xoá mất khoá
  // bí mật đang hiện cho doanh nghiệp copy, trước khi họ kịp lưu lại).
  const refreshLastTest = () => {
    setRefreshingTest(true);
    getBrandConversionSetup(campaignId)
      .then((next) => setSetup((prev) => (prev ? { ...prev, lastTest: next.lastTest } : next)))
      .catch((e) => toast.error(errorMessage(e, 'Không làm mới được.')))
      .finally(() => setRefreshingTest(false));
  };

  if (loading) {
    return (
      <div className="rounded-xl border ui-divider p-4">
        <div className="flex items-center justify-center p-6">
          <Loader2 className="animate-spin ui-muted" />
        </div>
      </div>
    );
  }

  if (!setup) return null;

  return (
    <div className="rounded-xl border ui-divider p-4 space-y-4">
      <div className="font-semibold ui-text">Cài mã đo chuyển đổi</div>

      <ol className="list-decimal space-y-1 pl-5 text-[13px] ui-text">
        <li>Dán đoạn mã dưới đây vào các trang muốn đo (trang đích, trang cảm ơn sau khi mua hàng…).</li>
        <li>
          Khi có đơn hàng hoàn tất, gọi{' '}
          <code className="rounded bg-black/5 px-1 py-0.5 dark:bg-white/10">
            cabConversion({'{'} orderId: &apos;MÃ_ĐƠN&apos;, value: 150000 {'}'})
          </code>{' '}
          trên trình duyệt, hoặc gọi API máy chủ bằng khoá bí mật như ví dụ bên dưới.
        </li>
        <li>Bấm &quot;Tạo lượt bấm thử&quot; để mở thử trang đích rồi kiểm tra ở mục &quot;Lần thử gần nhất&quot;.</li>
      </ol>

      <div className="space-y-1.5">
        <div className="text-[13px] font-semibold ui-text">Đoạn mã gắn vào trang web</div>
        <div className="flex items-start gap-2">
          <pre className="flex-1 overflow-x-auto whitespace-pre-wrap break-all rounded-lg bg-black/5 p-3 text-xs ui-text dark:bg-white/10">
            {setup.scriptSnippet}
          </pre>
          <button
            type="button"
            onClick={() => copyWithToast(setup.scriptSnippet, 'đoạn mã')}
            className="shrink-0 rounded-md border ui-divider p-2"
          >
            <Copy size={15} className="ui-text" />
          </button>
        </div>
      </div>

      <div className="space-y-1.5">
        <div className="text-[13px] font-semibold ui-text">Ví dụ gọi API máy chủ</div>
        <div className="flex items-start gap-2">
          <pre className="flex-1 overflow-x-auto whitespace-pre-wrap break-all rounded-lg bg-black/5 p-3 text-xs ui-text dark:bg-white/10">
            {setup.serverExample}
          </pre>
          <button
            type="button"
            onClick={() => copyWithToast(setup.serverExample, 'ví dụ API')}
            className="shrink-0 rounded-md border ui-divider p-2"
          >
            <Copy size={15} className="ui-text" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <div className="text-[13px] font-semibold ui-text">Khoá công khai</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 truncate rounded-lg bg-black/5 px-2 py-1.5 text-xs ui-text dark:bg-white/10">
              {setup.publicKey}
            </code>
            <button
              type="button"
              onClick={() => copyWithToast(setup.publicKey, 'khoá công khai')}
              className="shrink-0 rounded-md border ui-divider p-2"
            >
              <Copy size={15} className="ui-text" />
            </button>
          </div>
        </div>

        <div className="space-y-1">
          <div className="text-[13px] font-semibold ui-text">Khoá bí mật</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 truncate rounded-lg bg-black/5 px-2 py-1.5 text-xs ui-text dark:bg-white/10">
              {setup.secret ? setup.secret : maskSecret(setup.secretLast4)}
            </code>
            <button
              type="button"
              onClick={() => copyWithToast(setup.secret || setup.secretLast4, 'khoá bí mật')}
              className="shrink-0 rounded-md border ui-divider p-2"
            >
              <Copy size={15} className="ui-text" />
            </button>
          </div>
          {setup.secret && (
            <p className="text-[12px] font-semibold text-amber-600 dark:text-amber-400">
              Hãy lưu lại khoá này, Cab sẽ không hiện lại.
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={rotating}
          onClick={rotate}
          className="rounded-md border ui-divider px-3 py-1.5 text-sm font-semibold ui-text disabled:opacity-60"
        >
          {rotating ? 'Đang tạo lại…' : 'Tạo lại khoá'}
        </button>
        <button
          type="button"
          disabled={testingClick}
          onClick={testClick}
          className="rounded-md bg-[#0057FF] px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-60"
        >
          {testingClick ? 'Đang tạo…' : 'Tạo lượt bấm thử'}
        </button>
      </div>

      <div className="rounded-lg border ui-divider p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[13px] font-semibold ui-text">Lần thử gần nhất</div>
          <button
            type="button"
            onClick={refreshLastTest}
            disabled={refreshingTest}
            className="shrink-0 rounded-md border ui-divider p-1.5 disabled:opacity-60"
          >
            <RefreshCw size={14} className={`ui-text ${refreshingTest ? 'animate-spin' : ''}`} />
          </button>
        </div>
        {setup.lastTest ? (
          <div className="mt-1.5 text-[13px] ui-muted">
            <div>{formatDateTime(setup.lastTest.at)}</div>
            <div>
              Mã đơn thử: {setup.lastTest.orderId}
              {setup.lastTest.value != null && ` · Giá trị: ${fmtVnd(setup.lastTest.value)}`}
            </div>
          </div>
        ) : (
          <p className="mt-1.5 text-[13px] ui-muted">Chưa có lượt thử nào.</p>
        )}
      </div>
    </div>
  );
};

export default BrandConversionSetupPanel;
